'use client';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Trans } from '@kit/ui/trans';

import { parseRateLimitError } from '../lib/parse-rate-limit-error';

/**
 * @name AuthErrorAlert
 * @param error This error comes from Supabase as the code returned on errors
 * This error is mapped from the translation auth:errors.{error}
 * To update the error messages, please update the translation file
 * https://github.com/supabase/gotrue-js/blob/master/src/lib/errors.ts
 * @constructor
 */
export function AuthErrorAlert({
  error,
}: {
  error: Error | null | undefined | string;
}) {
  const { i18n } = useTranslation();

  if (!error) {
    return null;
  }

  const DefaultError = <Trans i18nKey="auth:errors.default" />;
  const message = error instanceof Error ? error.message : error;
  const code =
    error && typeof error === 'object' && 'code' in error
      ? (error as { code?: unknown }).code
      : undefined;

  // Check if this is a rate limit error
  const rateLimitInfo = parseRateLimitError(error);

  // Resolve the message key in priority order:
  //   1. rate-limit (parsed above),
  //   2. the stable Supabase error `code` (e.g. `weak_password`) when we have
  //      a translation for it,
  //   3. the human message string — several GoTrue errors are keyed by message
  //      (e.g. "User already registered", "Invalid login credentials").
  // Without the code-first lookup, coded failures such as leaked-password
  // rejection (`weak_password`) fell through to the generic default, which
  // reads "check your internet connection" — a misleading network message for
  // what is actually a password-policy rejection.
  const translationKey =
    rateLimitInfo.isRateLimit && rateLimitInfo.translationKey
      ? `auth:errors.${rateLimitInfo.translationKey}`
      : typeof code === 'string' && i18n.exists(`auth:errors.${code}`)
        ? `auth:errors.${code}`
        : `auth:errors.${message}`;

  const translationValues = rateLimitInfo.seconds
    ? { seconds: rateLimitInfo.seconds }
    : undefined;

  return (
    <Alert variant={'destructive'}>
      <ExclamationTriangleIcon className={'w-4'} />

      <AlertTitle>
        <Trans i18nKey={`auth:errorAlertHeading`} />
      </AlertTitle>

      <AlertDescription data-test={'auth-error-message'}>
        <Trans
          i18nKey={translationKey}
          values={translationValues}
          defaults={'<DefaultError />'}
          components={{ DefaultError }}
        />
      </AlertDescription>
    </Alert>
  );
}
